import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { DragIndicator as DragIndicatorIcon, Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import type { LocationProvider } from '../types/provider';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';

function sortProviders(list: LocationProvider[]): LocationProvider[] {
  return [...list].sort((a, b) => {
    const oa = a.public_menu_sort_order ?? 0;
    const ob = b.public_menu_sort_order ?? 0;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name, 'fr');
  });
}

const PartnerNavOrderPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<LocationProvider[]>([]);
  const [dirty, setDirty] = useState(false);
  const [draggedId, setDraggedId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['providers-nav-order'],
    queryFn: () => apiService.getProviders({ status: 'active', limit: 500 }),
  });

  useEffect(() => {
    if (data?.providers) {
      setItems(sortProviders(data.providers));
      setDirty(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateProvidersPublicMenuOrder(
        items.map((p, index) => ({ id: p.id, public_menu_sort_order: index }))
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['providers-nav-order'] });
      setDirty(false);
    },
  });

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setItems((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
    setDirty(true);
  }, []);

  const helper = useMemo(
    () =>
      'Cet ordre est utilisé dans le menu « Partenaires » du site (accueil, FAQ, fiches partenaires). Les partenaires inactifs n’apparaissent pas dans le menu. Glissez les lignes par la poignée ⋮⋮ pour réordonner.',
    []
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Ordre du menu Partenaires
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {helper}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          disabled={!dirty || saveMutation.isPending || items.length === 0}
          onClick={() => saveMutation.mutate()}
        >
          Enregistrer
        </Button>
      </Box>

      {error ? (
        <Alert severity="error">Impossible de charger les partenaires actifs.</Alert>
      ) : null}
      {saveMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors de l&apos;enregistrement de l&apos;ordre.
        </Alert>
      ) : null}
      {saveMutation.isSuccess && !dirty ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Ordre enregistré.
        </Alert>
      ) : null}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              Aucun partenaire actif.
            </Typography>
          ) : (
            <List disablePadding>
              {items.map((p, index) => {
                const isDragging = draggedId === p.id;
                return (
                  <ListItem
                    key={p.id}
                    divider={index < items.length - 1}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggedId == null || draggedId === p.id) return;
                      const fromIndex = items.findIndex((item) => item.id === draggedId);
                      if (fromIndex >= 0) reorder(fromIndex, index);
                      setDraggedId(null);
                    }}
                    sx={{
                      opacity: isDragging ? 0.55 : 1,
                      bgcolor: isDragging ? 'action.hover' : 'transparent',
                      transition: 'opacity 0.15s ease, background-color 0.15s ease',
                      pr: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={`Déplacer ${p.name}`}
                      draggable
                      onDragStart={(e) => {
                        setDraggedId(p.id);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(p.id));
                      }}
                      onDragEnd={() => setDraggedId(null)}
                      sx={{
                        cursor: 'grab',
                        mr: 0.5,
                        color: 'text.secondary',
                        flexShrink: 0,
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <DragIndicatorIcon />
                    </IconButton>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        src={resolveProviderLogoUrl(p.logo_url, p.logo) || undefined}
                        variant="rounded"
                        sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700 }}
                      >
                        {p.name?.charAt(0)?.toUpperCase() ?? '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={p.name}
                      secondary={`Position ${index + 1}`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartnerNavOrderPage;
